import React from 'react';
import logo from '../../../assets/images/logo.png';
import { frenchDateOnly } from '../../../variables/constants';
import BlocSignature from '../../../components/BlocSignature';

const G = '#1a6b3c';

const Divider = () => (
    <div style={{ height: '2px', background: `linear-gradient(to right, ${G}, #86efac)`, margin: '8px 0' }} />
);

const InfoLine = ({ label, value }) => (
    <tr>
        <td style={{ padding: '1mm 4mm 1mm 0', fontSize: '10pt', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
            {label} :
        </td>
        <td style={{ padding: '1mm 0', fontSize: '10pt', color: '#000' }}>
            {value || '-'}
        </td>
    </tr>
);

export default function NoteMissionExpert({ sinistre, affectation, signature = null }) {
    const today = new Date().toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
    });

    const isMedical = affectation?.expertTypeLabel === 'MEDICAL';
    const nomExpert = affectation?.expertNomComplet || '-';
    const civilite = isMedical ? 'Docteur' : 'Monsieur';
    const victimeNom = affectation?.victimeNomPrenom || '-';
    const numSinistre = sinistre?.numeroSinistreLocal || sinistre?.numSinistre || '-';
    const dateAccident = sinistre?.dateAccident ? frenchDateOnly(sinistre.dateAccident) : '-';
    // `dateAffectation` et `dateLimiteRapport` sont des LocalDate, donc serialisees
    // en ISO : sans ce formatage la note affiche « 2026-09-12 ».
    const dateAff = affectation?.dateAffectation ? frenchDateOnly(affectation.dateAffectation) : '-';
    const dateLimite = affectation?.dateLimiteRapport
        ? frenchDateOnly(affectation.dateLimiteRapport) : null;
    // La nature du dommage plutot que la valeur brute de l'enum TypeExpertise :
    // « INITIAL » ne dit rien a l'expert qui recoit la lettre. La distinction est
    // celle qui pilote deja « expertise medicale » / « automobile » dans le corps.
    const typeExpertise = isMedical ? 'CORPOREL' : 'MATÉRIEL';
    const lieuAccident = sinistre?.lieuAccident || '';
    const souscripteurNom = sinistre?.assuréNom || sinistre?.souscripteurNom || '';

    return (
        <div style={{
            fontFamily: "Calibri, 'Calibri Light', Arial, sans-serif",
            /* Zone utile d'une A4 : 210 mm moins les 20 mm de marge de chaque cote,
               declarees par @page a l'impression. Le document etait dessine en pixels
               (800 px) et l'impression compensait par un zoom a 0,78 - chaque taille
               en points sortait donc reduite de 22 %. En millimetres, un 11 pt est un
               vrai 11 pt. */
            width: '170mm',
            margin: '0 auto',
            padding: 0,
            lineHeight: 1.35,
            fontSize: '10pt',
            color: '#000000',
            background: '#ffffff',
        }}>

            {/*  En-tête  */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div>
                    <div style={{ fontSize: '15pt', fontWeight: 'bold', color: G, letterSpacing: '-0.5px' }}>
                        CBConnect
                    </div>
                    <div style={{ fontSize: '9pt', color: '#6b7280', marginTop: '2px' }}>
                        BNCB Togo - Gestion des sinistres
                    </div>
                </div>
                <img src={logo} alt="Logo CBConnect" style={{ height: '52px', objectFit: 'contain' }} />
            </div>
            <Divider />

            {/*  Référence  */}
            <div style={{ marginTop: '4mm', marginBottom: '5mm' }}>
                <span style={{ fontSize: '10pt', color: '#000' }}>
                    <strong>Réf :</strong> {numSinistre} / BNCB Togo
                </span>
            </div>

            {/*  Bloc expert (droite) + date  */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6mm' }}>
                <div style={{ textAlign: 'left', minWidth: '240px' }}>
                    <p style={{ margin: '0 0 2px 0', fontWeight: 'bold', fontSize: '11pt' }}>
                        {civilite} {nomExpert}
                    </p>
                    {affectation?.expertAdresse && (
                        <p style={{ margin: '0 0 2px 0', fontSize: '10pt' }}>
                            {affectation.expertAdresse}
                        </p>
                    )}
                    {affectation?.expertContact && (
                        <p style={{ margin: '0 0 2px 0', fontSize: '10pt' }}>
                            <strong>Tél :</strong> {affectation.expertContact}
                        </p>
                    )}
                    {affectation?.expertEmail && (
                        <p style={{ margin: '0 0 2px 0', fontSize: '10pt' }}>
                            <strong>E-mail :</strong> {affectation.expertEmail}
                        </p>
                    )}
                    <div style={{ borderBottom: '1px solid #000', margin: '8px 0' }} />
                    <p style={{ margin: '6px 0 0 0', fontSize: '10pt' }}>
                        Lomé, le {today}
                    </p>
                </div>
            </div>

            {/*  Objet  */}
            <div style={{ marginBottom: '5mm' }}>
                <p style={{ margin: 0, fontSize: '10pt' }}>
                    <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>Objet :</span>{' '}
                    <strong>
                        Note de mission - SIN N° {numSinistre}
                        {souscripteurNom ? ` - ${souscripteurNom}` : ''}
                        {' '}/ {victimeNom}
                    </strong>
                </p>
                <p style={{ margin: '4px 0 0 52px', fontSize: '10pt' }}>
                    <strong>Accident du {dateAccident}{lieuAccident ? ` - ${lieuAccident}` : ''}</strong>
                </p>
            </div>

            {/*  Corps  */}
            <div style={{ textAlign: 'justify', fontSize: '10pt', lineHeight: 1.4 }}>
                <p style={{ marginBottom: '14px' }}>
                    {civilite},
                </p>

                <p style={{ marginBottom: '14px' }}>
                    Dans le cadre du traitement du dossier sinistre référencé en marge, nous avons
                    l'honneur de vous confier la mission d'expertise {isMedical ? 'médicale' : 'automobile'} suivante :
                </p>

                {/* Tableau récap mission */}
                <div style={{
                    border: `1px solid ${G}33`,
                    borderRadius: '6px',
                    padding: '3mm 4mm',
                    marginBottom: '18px',
                    background: '#f0fdf4',
                }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <tbody>
                            <InfoLine label="Victime" value={victimeNom} />
                            <InfoLine label="Type d'expertise" value={typeExpertise} />
                            <InfoLine label="Date d'affectation" value={dateAff} />
                            {dateLimite && (
                                <InfoLine label="Date limite rapport" value={dateLimite} />
                            )}
                            {affectation?.observations && (
                                <InfoLine label="Observations" value={affectation.observations} />
                            )}
                        </tbody>
                    </table>
                </div>

                {isMedical ? (
                    <>
                        <p style={{ marginBottom: '14px' }}>
                            Nous vous prions de bien vouloir procéder à l'examen médical de la victime susnommée
                            et de nous adresser votre rapport d'expertise médicale dans les meilleurs délais
                            {dateLimite && <>, et au plus tard le <strong>{dateLimite}</strong></>}.
                            Votre rapport devra préciser notamment : le taux d'incapacité permanente partielle (IPP),
                            la durée d'incapacité temporaire totale (ITT), le pretium doloris, le préjudice
                            esthétique ainsi que tout autre chef de préjudice prévu par le Code CIMA.
                        </p>
                        <p style={{ marginBottom: '14px' }}>
                            Vous trouverez ci-joint les pièces médicales disponibles au dossier
                            (certificats médicaux, bilans, ordonnances…) pour faciliter votre mission.
                        </p>
                    </>
                ) : (
                    <>
                        <p style={{ marginBottom: '14px' }}>
                            Nous vous prions de bien vouloir procéder à l'évaluation des dommages matériels
                            liés au sinistre susmentionné et de nous adresser votre rapport d'expertise dans les
                            meilleurs délais{dateLimite && <>, et au plus tard le <strong>{dateLimite}</strong></>}.
                            Votre rapport devra préciser la nature des dommages, le montant des réparations,
                            la valeur à dire d'expert et vos honoraires.
                        </p>
                        <p style={{ marginBottom: '14px' }}>
                            Vous trouverez ci-joint les pièces disponibles au dossier pour faciliter
                            votre intervention (constat amiable, photos des dommages, devis…).
                        </p>
                    </>
                )}

                <p style={{ marginBottom: '14px' }}>
                    Dans l'attente des conclusions de votre rapport d'expertise, nous vous prions
                    d'agréer, {civilite}, l'expression de nos salutations distinguées.
                </p>
            </div>

            {/*  Signature  */}
            <div style={{ marginTop: '6mm', textAlign: 'right', marginBottom: '3mm' }}>
                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '10pt', textDecoration: 'underline' }}>
                    La Direction
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '10pt' }}>
                    BNCB Togo
                </p>
                {/* CE DOCUMENT NE RÉSERVE AUCUN ESPACE DE SIGNATURE : contrairement aux
                    attestations, la hauteur ajoutée ici s'ajoute vraiment. La mesure du
                    15/09 donnait 247 mm pour 261 mm utilisables, soit 14 mm de marge.
                    D'où 10 mm, qui en laisse 4. Augmenter cette valeur sans remesurer
                    ferait déborder sur une seconde page ne portant que le pied de page. */}
                <BlocSignature
                    codeDocument="NOTE_MISSION_EXPERT"
                    sinistreTrackingId={sinistre?.sinistreTrackingId}
                    hauteurMm={10}
                    signature={signature}
                />
            </div>

            {/*  Pièces jointes  */}
            <div>
                <p style={{ margin: 0, fontSize: '10pt' }}>
                    <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>P.j :</span>{' '}
                    {isMedical
                        ? 'Certificats médicaux - Pièces du dossier'
                        : 'Constat amiable - Devis de réparation - Photos des dommages'}
                </p>
            </div>

            {/*  Pied de page  */}
            <div style={{
                marginTop: '4mm',
                paddingTop: '2mm',
                borderTop: '1px solid #e5e7eb',
                fontSize: '9pt',
                color: '#9ca3af',
                textAlign: 'center',
            }}>
                BNCB Togo - Document généré par CBConnect le {today}
            </div>
        </div>
    );
}
